import React, { useMemo, useState } from "react";
import { Calculator, TrendingDown, PiggyBank, Boxes } from "lucide-react";

const fmt = (n) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M` : `$${Math.round(n / 1000)}K`;
const fmtFull = (n) => `$${Math.round(n).toLocaleString()}`;

export function ROICalculator({ onGetStarted }) {
  const [spend, setSpend] = useState(2_500_000);
  const [deadPct, setDeadPct] = useState(18);

  const { deadValue, recovered, freedPct } = useMemo(() => {
    const dead = spend * (deadPct / 100);
    return {
      deadValue: dead,
      recovered: dead * 0.3, // AssortIQ typically cuts ~30% of dead inventory
      freedPct: Math.round(deadPct * 0.3),
    };
  }, [spend, deadPct]);

  return (
    <section id="roi" className="mx-auto max-w-7xl px-6 py-20">
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] overflow-hidden">
        <div className="grid lg:grid-cols-2">
          {/* INPUTS */}
          <div className="p-8 sm:p-12">
            <div className="inline-flex items-center gap-2 rounded-full bg-ai/10 text-ai px-3 py-1 text-xs font-bold uppercase tracking-wider">
              <Calculator className="h-3.5 w-3.5" /> ROI calculator
            </div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl font-extrabold leading-tight">
              How much dead inventory could you cut?
            </h2>
            <p className="mt-3 text-white/60 leading-relaxed">
              Slow movers tie up cash and shelf space. Drag the sliders to see what AssortIQ could free up in a year.
            </p>

            <div className="mt-8 space-y-8">
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="roi-spend" className="text-sm font-semibold text-white/80">Annual inventory spend</label>
                  <span data-testid="roi-spend-value" className="font-heading text-lg font-extrabold text-ai">{fmt(spend)}</span>
                </div>
                <input
                  id="roi-spend"
                  data-testid="roi-spend-slider"
                  type="range" min={100_000} max={20_000_000} step={100_000}
                  value={spend} onChange={(e) => setSpend(Number(e.target.value))}
                  className="mt-3 w-full accent-[#FF5E00] cursor-pointer"
                />
                <div className="mt-1 flex justify-between text-[11px] text-white/40"><span>$100K</span><span>$20M</span></div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="roi-dead" className="text-sm font-semibold text-white/80">Slow / dead-moving stock</label>
                  <span data-testid="roi-dead-value" className="font-heading text-lg font-extrabold text-ai">{deadPct}%</span>
                </div>
                <input
                  id="roi-dead"
                  data-testid="roi-dead-slider"
                  type="range" min={5} max={40} step={1}
                  value={deadPct} onChange={(e) => setDeadPct(Number(e.target.value))}
                  className="mt-3 w-full accent-[#FF5E00] cursor-pointer"
                />
                <div className="mt-1 flex justify-between text-[11px] text-white/40"><span>5%</span><span>40%</span></div>
              </div>
            </div>
          </div>

          {/* RESULT */}
          <div className="relative p-8 sm:p-12 bg-gradient-to-br from-ai/15 to-transparent border-t lg:border-t-0 lg:border-l border-white/10">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,94,0,0.15),transparent_60%)]" />
            <div className="relative">
              <div className="text-sm text-white/60">Estimated cash you could recover in year one</div>
              <div data-testid="roi-recovered" className="mt-2 font-heading text-5xl sm:text-6xl font-extrabold text-ai leading-none">
                {fmtFull(recovered)}
              </div>
              <p className="mt-3 text-xs text-white/45">Based on typically cutting ~30% of slow-moving stock. Illustrative estimate.</p>

              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-forest-900/40 p-4">
                  <span className="h-9 w-9 rounded-lg bg-action-remove/15 text-action-remove flex items-center justify-center shrink-0"><TrendingDown className="h-5 w-5" /></span>
                  <div>
                    <div className="text-sm font-semibold">{fmtFull(deadValue)} tied up in dead inventory today</div>
                    <div className="text-xs text-white/50">Cash sitting on shelves that isn't turning</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-forest-900/40 p-4">
                  <span className="h-9 w-9 rounded-lg bg-action-keep/15 text-action-keep flex items-center justify-center shrink-0"><PiggyBank className="h-5 w-5" /></span>
                  <div>
                    <div className="text-sm font-semibold">{fmtFull(recovered)} back in working capital</div>
                    <div className="text-xs text-white/50">Redeploy into products that actually sell</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-forest-900/40 p-4">
                  <span className="h-9 w-9 rounded-lg bg-ai/15 text-ai flex items-center justify-center shrink-0"><Boxes className="h-5 w-5" /></span>
                  <div>
                    <div className="text-sm font-semibold">~{freedPct}% more shelf & display space freed</div>
                    <div className="text-xs text-white/50">Room for high-velocity items and new lines</div>
                  </div>
                </div>
              </div>

              <button
                data-testid="roi-cta"
                onClick={onGetStarted}
                className="mt-8 w-full rounded-full bg-ai px-6 py-3.5 text-sm font-bold uppercase tracking-wider hover:bg-[#e05400] transition-all hover:-translate-y-0.5"
              >
                See your real numbers — start free
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
